"use client"

import { useSyncExternalStore } from "react"

/**
 * A page with an action bar along the bottom edge says so ON THE PAGE, and the launcher rests above that band.
 *
 * The route rule (isMutationRoute, use-fab-auto-corner.ts) cannot see every such page: a draft survey is `/surveys/<id>`, the same address as its
 * read-only overview, and its Back / Save draft / Publish row ends 24 px from the corner where the 48 px launcher
 * rests (QA, 17 Oct, 1440x900: a click on Publish landed on the launcher). So the launcher reads the page instead:
 * an element carrying `data-fab-actions` (add it to the page's root or to the bar itself) means "my primary action is
 * along the bottom edge". The survey builder's step bar, `data-survey-step-bar`, is the same statement for the
 * builder, which already carries it. Prefer this to a per-page `pe-24` on the bar: the bar keeps its own layout and
 * the launcher moves out of its way, in every language.
 *
 * Put `data-fab-actions` on the BAR ITSELF when it is in the page's flow (the survey builder's footer row): the launcher then
 * clears the rectangle the bar really has on screen, wherever scrolling or a short page puts it (hooks/use-fab-bar-lift.ts).
 * A marker that is only a loose "my actions are at the bottom" (the page, or the builder's step bar, which is at the TOP) still
 * lifts the launcher by one fixed band (FAB_ACTION_BAND), which suits a bar pinned to the window's edge and nothing else.
 */
export const FAB_ACTIONS_ATTR = "data-fab-actions"
export const FAB_ACTIONS_SELECTOR = `[${FAB_ACTIONS_ATTR}], [data-survey-step-bar]`

function pageHasBottomActions(): boolean {
  return typeof document !== "undefined" && document.querySelector(FAB_ACTIONS_SELECTOR) !== null
}

/**
 * One observer for every reader (the host, the launcher and the feedback widget each call the hook), started with the
 * first and stopped with the last. It only tells readers to look again; each reads the page itself, so what a reader
 * sees is never a stale copy. A route change adds or removes the marked element, so it needs no pathname of its own.
 */
const actionsListeners = new Set<() => void>()
let actionsObserver: MutationObserver | null = null

export function subscribeToBottomActions(listener: () => void): () => void {
  actionsListeners.add(listener)
  if (!actionsObserver && typeof MutationObserver !== "undefined" && typeof document !== "undefined") {
    actionsObserver = new MutationObserver(() => actionsListeners.forEach((notify) => notify()))
    actionsObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [FAB_ACTIONS_ATTR, "data-survey-step-bar"],
    })
  }
  return () => {
    actionsListeners.delete(listener)
    if (actionsListeners.size === 0 && actionsObserver) {
      actionsObserver.disconnect()
      actionsObserver = null
    }
  }
}

/** Does the page on screen carry a bottom action bar? False on the server and until the client has read the page. */
export function usePageHasBottomActions(): boolean {
  return useSyncExternalStore(subscribeToBottomActions, pageHasBottomActions, () => false)
}
